import { useQuery } from '@apollo/client'
import { useDebouncedValue } from '@mantine/hooks'

import { SEARCH_CARRIERS } from 'src/graphql/carriers.graphql'

import type { CarrierSearchResult } from '../carrier.types'

export const MINIMUM_SEARCH_CHARACTERS = 2
const SEARCH_RESULT_LIMIT = 25
const DEBOUNCE_MS = 300

/**
 * Turns a raw search box value into carrier results.
 *
 * Owns the debounce and the minimum-length rule so the field stays a dumb
 * input, and so both are stated in one place rather than inside a component.
 */
export const useCarrierSearch = (input: string) => {
  const [debouncedInput] = useDebouncedValue(input, DEBOUNCE_MS)

  const term = debouncedInput.trim()
  const awaitingInput = term.length < MINIMUM_SEARCH_CHARACTERS

  const { data, loading, error } = useQuery(SEARCH_CARRIERS, {
    variables: {
      filters: { search: term },
      pageInfo: { limit: SEARCH_RESULT_LIMIT, offset: 0 },
    },
    skip: awaitingInput,
    fetchPolicy: 'cache-and-network',
  })

  const results: CarrierSearchResult[] = data?.carriers?.items ?? []

  // The box updates on every keystroke but the query only re-runs after the
  // debounce, so for 300ms the previous results sit under the new text. Count
  // that gap as loading; otherwise the old list reads as the answer to a term
  // it was never searched for.
  const settling = input.trim() !== term

  return {
    term,
    awaitingInput,
    results,
    totalCount: data?.carriers?.totalCount ?? 0,
    error,
    // A skipped query never loads; reporting it as loading would leave the
    // spinner on forever while the box is empty.
    loading: (loading || settling) && !awaitingInput,
  }
}
